import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  ViewChild,
  ViewChildren,
  ElementRef,
  QueryList,
  signal,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  Estimate,
  FuzzyBreakdown,
  FuzzyBreakdownEstimate,
  VendorDetails,
  VendorOrder,
  Cost,
} from '../../../../../../shared/models/vendor-order.model';
import { CanvasService } from '../../../../../../shared/services/canvas.service';
import { SpinnerComponent } from '../../../../../../shared/components/az-spinner/spinner.component';
import { DownloadSuccessDialogComponent } from '../../../../../../shared/components/download-success-dialog/download-success-dialog.component';
import { Locale, LOCALES } from '../../../../../../shared/config/locales';
import jsPDF from 'jspdf';

export interface SowModalData {
  order: VendorOrder;
  estimate: Estimate;
  breakdown?: FuzzyBreakdown;
  vendor?: VendorDetails;
}

@Component({
  selector: 'app-quote-sow-modal',
  standalone: true,
  imports: [CurrencyPipe, MatDialogContent, SpinnerComponent],
  templateUrl: './quote-sow-modal.component.html',
  styleUrl: './quote-sow-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteSowModalComponent {
  private readonly dialogRef = inject(MatDialogRef<QuoteSowModalComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly canvasService = inject(CanvasService);
  private readonly translateService = inject(TranslateService);
  readonly data: SowModalData = inject(MAT_DIALOG_DATA);

  @ViewChild('pdfContent') pdfContent!: ElementRef<HTMLElement>;
  @ViewChildren('breakdownTableEl') breakdownTables!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('disclaimerEl') disclaimerElement?: ElementRef<HTMLElement>;

  readonly loading = signal(false);

  readonly order = computed(() => this.data.order);
  readonly estimate = computed(() => this.data.estimate);
  readonly vendor = computed(() => this.data.vendor);
  readonly breakdown = computed(() => this.data.breakdown);

  readonly vendorName = computed(() => this.estimate().name ?? 'Unknown Vendor');

  readonly sowCreationDate = computed(() => {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  });

  readonly currency = computed(() => {
    const est = this.estimate();
    return est.currency ?? est.quote?.normal?.currency ?? 'USD';
  });

  readonly jobName = computed(() => this.order().customJobName ?? '--');
  readonly jobNumber = computed(() => {
    const costId = this.estimate().costId;
    return costId ? `AZT-${costId}` : (this.order().jobNumber ?? '--');
  });

  readonly preferredDeliveryDate = computed(() => {
    const dl = this.order().deadline ?? this.order().fixedDeadline;
    if (!dl) return '--';
    const d = new Date(dl);
    if (Number.isNaN(d.getTime())) return '--';
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  });

  readonly supplierName = computed(() => this.estimate().name ?? '--');
  readonly contactEmail = computed(() => 'AZTranslate@astrazeneca.com');

  readonly vendorLegalName = computed(
    () => this.vendor()?.legalEntityName ?? this.vendorName(),
  );
  readonly vendorAddress = computed(() => this.vendor()?.address);

  readonly sourceLocale = computed(() => this.order().sourceLocaleId ?? '');
  readonly targetLocales = computed(() => {
    const targets = this.order().targetLocaleId;
    if (!targets) return '';
    return Array.isArray(targets) ? targets.join(', ') : targets;
  });

  readonly languageDirection = computed(() => {
    const source = this.sourceLocale();
    const target = this.targetLocales();
    if (!source && !target) return '';
    const sourceName = this.getLanguageName(source);
    const targets = this.order().targetLocaleId;
    const targetNames = Array.isArray(targets)
      ? targets.map(t => this.getLanguageName(t)).sort().join(', ')
      : this.getLanguageName(target);
    return `${sourceName} → ${targetNames}`;
  });

  readonly costs = computed<Cost[]>(() => {
    const est = this.estimate();
    const quote = est.quote?.normal ?? est.quote?.rushed;
    if (quote?.costs?.length) return quote.costs;
    const totalCost = quote?.totalCost ?? est.priceMax ?? est.priceMin;
    if (totalCost != null) {
      return [{ concept: 'Estimated linguistic review', cost: totalCost }];
    }
    return [];
  });

  readonly totalCost = computed(() => {
    const est = this.estimate();
    const quote = est.quote?.normal ?? est.quote?.rushed;
    return quote?.totalCost ?? est.priceMax ?? est.priceMin ?? 0;
  });

  readonly deliveryDays = computed(() => this.order().deliveryDays ?? 1);

  // Breakdown table configuration
  readonly breakdownCols = [
    'Source Language', 'Target Language', 'Step Name', 'Currency Cost',
    'Word Count', 'Fuzzy Range Code', 'Estimated Cost Min', 'Estimated Cost Max',
  ];
  readonly numericCols = [
    'Word Count', 'Weighted Word Count', 'Character Count',
    'Estimated Cost Min', 'Estimated Cost Max',
  ];
  readonly subtotalRow = 'Subtotal';
  readonly hiddenColsForSubtotalRow = ['Source Language', 'Target Language'];

  readonly projectName = computed(() =>
    this.breakdown()?.['Project Name'] ?? this.order().xtmProjectName ?? '',
  );

  readonly smartlingJobName = computed(() => this.order().smartlingJobName ?? '');

  readonly disclaimer = computed(() => {
    const v = this.vendor();
    if (!v?.disclaimer) return '';
    return v.disclaimer.replace(/\n/g, '<br />');
  });

  readonly sortedEstimateEntries = computed(() => {
    const estimates = this.breakdown()?.estimates;
    if (!estimates) return [];
    return Object.entries(estimates).sort(([keyA], [keyB]) => {
      const localeA = LOCALES.find((l: Locale) => l.code === keyA || l.xtmCode === keyA);
      const localeB = LOCALES.find((l: Locale) => l.code === keyB || l.xtmCode === keyB);
      return (localeA?.name ?? keyA).localeCompare(localeB?.name ?? keyB);
    });
  });

  getLanguageName(code: string): string {
    const locale = LOCALES.find((l: Locale) => l.code === code || l.xtmCode === code);
    if (locale) return locale.name;
    const resolved = this.resolveLocaleCode(code);
    const key = `languages.${resolved}`;
    const result = this.translateService.instant(key);
    return result !== key ? result : code;
  }

  isNumericCol(col: string): boolean {
    return this.numericCols.includes(col);
  }

  isSubtotalRow(estimate: FuzzyBreakdownEstimate, isLast: boolean): boolean {
    return isLast && estimate['Fuzzy Range Code'] === this.subtotalRow;
  }

  isHiddenForSubtotal(col: string): boolean {
    return this.hiddenColsForSubtotalRow.includes(col);
  }

  getCellValue(estimate: FuzzyBreakdownEstimate, col: string, isLast: boolean): string {
    if (this.isSubtotalRow(estimate, isLast) && this.isHiddenForSubtotal(col)) return '';
    const value = (estimate as unknown as Record<string, string>)[col] ?? '';
    if (this.numericCols.includes(col) && value) {
      const num = parseFloat(value);
      return isNaN(num) ? value : num.toLocaleString();
    }
    return value;
  }

  private resolveLocaleCode(key: string): string {
    const locale = LOCALES.find((l: Locale) => l.code === key || l.xtmCode === key);
    return locale?.code ?? key;
  }

  async generateQuotePDF(): Promise<void> {
    this.loading.set(true);

    try {
      const margin = 2;
      const fileWidth = 210 - 2 * margin;
      const pageHeight = 295;
      const offset = 5;
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Main content
      const canvas = await this.canvasService.createCanvasFromHTML(this.pdfContent.nativeElement);
      const imageData = canvas.toDataURL('image/jpeg', 1);
      const generatedImageHeight = (canvas.height * fileWidth) / canvas.width;
      let position = offset;
      let heightLeft = generatedImageHeight - pageHeight;

      pdf.addImage(imageData, 'PNG', margin, position, fileWidth, generatedImageHeight);

      while (heightLeft >= 0) {
        position = heightLeft - generatedImageHeight;
        pdf.addPage();
        pdf.addImage(imageData, 'PNG', margin, position, fileWidth, generatedImageHeight);
        heightLeft -= pageHeight;
      }

      // Breakdown tables
      if (this.breakdownTables?.length) {
        for (const table of this.breakdownTables) {
          const breakdownCanvas = await this.canvasService.createCanvasFromHTML(table.nativeElement);
          const breakdownImg = breakdownCanvas.toDataURL('image/jpeg', 1);
          const breakdownImgHeight = (breakdownCanvas.height * fileWidth) / breakdownCanvas.width;
          pdf.addPage();
          pdf.addImage(breakdownImg, 'PNG', margin, offset, fileWidth, breakdownImgHeight);
        }
      }

      // Disclaimer
      if (this.disclaimerElement) {
        const disclaimerCanvas = await this.canvasService.createCanvasFromHTML(this.disclaimerElement.nativeElement);
        const disclaimerImg = disclaimerCanvas.toDataURL('image/jpeg', 1);
        const disclaimerImgHeight = (disclaimerCanvas.height * fileWidth) / disclaimerCanvas.width;
        pdf.addPage();
        pdf.addImage(disclaimerImg, 'PNG', margin, offset, fileWidth, disclaimerImgHeight);
      }

      const fileName = (this.order().customJobName ?? 'quote-estimate') + '.pdf';
      pdf.save(fileName);
      this.showDownloadSuccess();
    } finally {
      this.loading.set(false);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  async download(): Promise<void> {
    await this.generateQuotePDF();
  }

  private showDownloadSuccess(): void {
    this.dialog.open(DownloadSuccessDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }
}
